import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
})

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface GoalEvent {
  _id: string
  student:
    | string
    | {
        _id: string
        firstName: string
        lastName: string
      }
  teacher:
    | string
    | {
        _id: string
        firstName: string
        lastName: string
      }
  goal:
    | string
    | {
        _id: string
        description: string
        category: string
        area: string
        objectives: string[]
      }
  period: string
  objectivesWithStatus: Array<{
    objective: string
    status: string
  }>
  status: string
  levelOfIndependence: string
  notes?: string
  workSamples: string[]
  createdAt: string
  updatedAt: string
}

export interface CreateGoalEventRequest {
  student: string
  teacher: string
  goal: string
  period: string
  objectivesWithStatus: Array<{
    objective: string
    status: string
  }>
  status: string
  levelOfIndependence: string
  notes: string
  workSamples: string[]
}

interface GoalEventResponse {
  success: boolean
  data: GoalEvent
}

// Get all goal events for the authenticated user's students
export const getAllGoalEvents = async (): Promise<GoalEvent[]> => {
  const response = await api.get('/goal-events')
  return response.data.data
}

// Get goal events for a specific goal
export const getGoalEventsByGoal = async (
  goalId: string,
): Promise<GoalEvent[]> => {
  const response = await api.get('/goal-events', { params: { goalId } })
  return response.data.data
}

// Get goal events for a specific student
export const getGoalEventsByStudent = async (
  studentId: string,
): Promise<GoalEvent[]> => {
  const response = await api.get('/goal-events', { params: { studentId } })
  return response.data.data
}

export const getGoalEvent = async (id: string): Promise<GoalEventResponse> => {
  const response = await api.get(`/goal-events/${id}`)
  return response.data
}

export const createGoalEvent = async (
  goalEventData: CreateGoalEventRequest,
): Promise<GoalEventResponse> => {
  const response = await api.post('/goal-events', goalEventData)
  return response.data
}

export const updateGoalEvent = async (
  id: string,
  goalEventData: CreateGoalEventRequest,
): Promise<GoalEventResponse> => {
  const response = await api.put(`/goal-events/${id}`, goalEventData)
  return response.data
}

export const deleteGoalEvent = async (
  id: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/goal-events/${id}`)
  return response.data
}
